/**
 * EligibilityGroupedRenderer — Wave 6 Slice 36 (2026-05-02).
 *
 * The eligibility section asks ~70 yes/no screening questions (criminal
 * history, security, immigration violations, removal proceedings,
 * unlawful presence, etc.). Most applicants legitimately answer "No"
 * to all of them — but rendering 70 stacked Yes/No widgets creates
 * decision fatigue, autopilot risk, and a scary "wall of legal
 * disclosure" feel.
 *
 * Pattern C ("grouped checklists with all-clear toggles") groups
 * conceptually-related questions and presents each group as a
 * collapsible block with a primary "None of these apply" path:
 *
 *   ▸ Criminal history (12 questions)
 *     Past arrests, convictions, drug offenses, etc.
 *     [ Yes — show me ]   [ No — none apply ]
 *
 * Click "No — none apply": every yes/no field in the group is set to
 * `false` on the engine, the block collapses to a "✓ None of these
 * apply" state with an "Edit" affordance. Click "Yes — show me": the
 * block expands and renders each question individually.
 *
 * Net result: a 70-question wall becomes an 8-row checklist on first
 * view. No autopilot, full granularity preserved on demand.
 *
 * Boolean (yes/no) fields go through the grouping. Non-boolean fields
 * (selects, dates, text in the eligibility section like
 * `eligibility.public_charge.*` brackets) render normally — they're
 * not the same UX problem.
 */
import { useEffect, useMemo, useState } from 'react';

import type {
  FieldDefinition,
  FieldId,
  ValidationIssue,
} from '@inside-us-aos/domain';
import type { FormEngine } from '@inside-us-aos/form-engine';

import { FieldRenderer } from './FieldRenderer';
import { withUserHelpText } from './userHelpText';

import styles from './EligibilityGroupedRenderer.module.css';

interface IGroupConfig {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  /** Field id prefix that places a field in this group. */
  readonly prefix: string;
}

const GROUPS: readonly IGroupConfig[] = [
  {
    id: 'immigration_violations',
    label: 'Immigration violations',
    description:
      'Past visa violations, false claims, fraud, or smuggling history.',
    prefix: 'eligibility.immigration_violations.',
  },
  {
    id: 'unlawful_presence',
    label: 'Unlawful presence',
    description:
      'Time spent in the U.S. without authorization, departures, and re-entries.',
    prefix: 'eligibility.unlawful_presence.',
  },
  {
    id: 'removal',
    label: 'Removal & deportation',
    description:
      'Past removal proceedings, deportation orders, voluntary departure, etc.',
    prefix: 'eligibility.removal.',
  },
  {
    id: 'prior_admission',
    label: 'Prior admission denials',
    description:
      'Past denied entries, refused visas, status violations at the border.',
    prefix: 'eligibility.prior_admission.',
  },
  {
    id: 'j_exchange_visitor',
    label: 'J-visa exchange-visitor rules',
    description:
      'Two-year home residency requirement and waivers (skip if you never held a J visa).',
    prefix: 'eligibility.j_exchange_visitor.',
  },
  {
    id: 'criminal',
    label: 'Criminal history',
    description:
      'Arrests, convictions, drug offenses, prostitution, money-laundering, gambling, trafficking.',
    prefix: 'eligibility.criminal.',
  },
  {
    id: 'security',
    label: 'Security & terrorism',
    description:
      'Affiliations with armed groups, weapons training, espionage, torture, child soldiers.',
    prefix: 'eligibility.security.',
  },
  {
    id: 'miscellaneous',
    label: 'Miscellaneous',
    description:
      'Polygamy intent, child abduction assistance, unlawful voting, citizenship-renunciation for tax avoidance, military desertion.',
    prefix: 'eligibility.miscellaneous.',
  },
  {
    // User audit flagged Public Charge as
    // missing from the eligibility section. The headline boolean
    // (`is_subject_to_inadmissibility_ground`, item 61) and the two
    // benefit-related follow-ups (items 68.a and 68.b) are routed
    // here so they render under a labeled heading USCIS reviewers
    // recognize, instead of disappearing into "Other questions". The
    // bracket selects (62–66) and the repeatable receipts groups
    // (68.c, 68.d) are non-boolean and continue to render in
    // "Additional details" via the standard render-unit path —
    // their item-numbered labels keep the connection clear.
    id: 'public_charge',
    label: 'Public charge',
    description:
      'Whether you are subject to the public-charge ground of inadmissibility (INA 212(a)(4)) and whether you have ever received certain public benefits or long-term institutional care.',
    prefix: 'eligibility.public_charge.',
  },
];

export interface IEligibilityGroupedRendererProps {
  engine: FormEngine;
  visibleFields: readonly FieldDefinition[];
  touchedFieldIds: ReadonlySet<string>;
  onTouchKey: (key: string) => void;
  issuesByFieldId: ReadonlyMap<string, readonly ValidationIssue[]>;
}

export function EligibilityGroupedRenderer(
  props: IEligibilityGroupedRendererProps,
): JSX.Element {
  const {
    engine,
    visibleFields,
    touchedFieldIds,
    onTouchKey,
    issuesByFieldId,
  } = props;

  // Bucket fields. EligibilityGroupedRenderer is intentionally
  // boolean-only: SectionRenderer filters non-boolean and repeatable
  // fields out before passing visibleFields here, and renders them
  // separately via the standard render-unit machinery (which knows how
  // to mount RepeatableGroupRenderer with per-instance state). Sending
  // a repeatable field through `<FieldRenderer>` here would crash —
  // engine.getAnswer/setAnswer throws when called on a repeatable
  // field without a groupInstanceId.
  //
  // Each visible field is either:
  //   - A boolean field that matches a group prefix → goes in that group
  //   - A boolean field that doesn't match any group → renders as a
  //     standalone yes/no below the groups
  //   - A non-boolean field that slipped through → defensive console
  //     warning, dropped from render. (Should never happen in prod;
  //     SectionRenderer is the sole caller and pre-filters.)
  const { grouped, ungroupedBoolean } = useMemo(() => {
    const grouped = new Map<string, FieldDefinition[]>();
    const ungroupedBoolean: FieldDefinition[] = [];

    for (const field of visibleFields) {
      if (field.valueType !== 'boolean') {
        // Defensive: caller contract violated. Don't render — the
        // alternative is a runtime crash for repeatable-group fields
        // and a misleading singleton answer slot for plain non-
        // booleans.
        // eslint-disable-next-line no-console
        console.warn(
          `[EligibilityGroupedRenderer] non-boolean field ${String(field.id)} skipped — caller must filter to boolean fields only.`,
        );
        continue;
      }
      const fid = field.id as string;
      const match = GROUPS.find(g => fid.startsWith(g.prefix));
      if (match) {
        const list = grouped.get(match.id) ?? [];
        list.push(field);
        grouped.set(match.id, list);
      } else {
        ungroupedBoolean.push(field);
      }
    }

    return { grouped, ungroupedBoolean };
  }, [visibleFields]);

  return (
    <div className={styles.shell}>
      <div className={styles.intro}>
        <p className={styles.intro__line}>
          A series of yes/no questions USCIS asks every applicant.{' '}
          <strong>For most people the answer is no across the board.</strong>{' '}
          Walk through each group below — clicking &quot;No — none apply&quot;
          is the fastest path when nothing in a group affects you.
        </p>
      </div>

      <div className={styles.groups}>
        {GROUPS.map(group => {
          const fields = grouped.get(group.id);
          if (!fields || fields.length === 0) return null;
          return (
            <EligibilityGroupBlock
              key={group.id}
              group={group}
              fields={fields}
              engine={engine}
              touchedFieldIds={touchedFieldIds}
              onTouchKey={onTouchKey}
              issuesByFieldId={issuesByFieldId}
            />
          );
        })}
      </div>

      {ungroupedBoolean.length > 0 ? (
        <div className={styles.ungrouped}>
          <h3 className={styles.ungrouped__heading}>Other questions</h3>
          {ungroupedBoolean.map(field => (
            <FieldRenderer
              key={field.id}
              field={withUserHelpText(field)}
              engine={engine}
              touched={touchedFieldIds.has(field.id)}
              onTouch={() => onTouchKey(field.id)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface IEligibilityGroupBlockProps {
  group: IGroupConfig;
  fields: readonly FieldDefinition[];
  engine: FormEngine;
  touchedFieldIds: ReadonlySet<string>;
  onTouchKey: (key: string) => void;
  issuesByFieldId: ReadonlyMap<string, readonly ValidationIssue[]>;
}

type TBlockState = 'unanswered' | 'all_no' | 'expanded';

function deriveInitialState(
  engine: FormEngine,
  fields: readonly FieldDefinition[],
): TBlockState {
  let hasYes = false;
  let allAnswered = true;
  for (const field of fields) {
    const v = engine.getAnswer(field.id);
    if (v === undefined) {
      allAnswered = false;
      continue;
    }
    if (v.type === 'boolean' && v.value === true) {
      hasYes = true;
    }
  }
  if (hasYes) return 'expanded';
  if (allAnswered) return 'all_no';
  return 'unanswered';
}

function EligibilityGroupBlock(
  props: IEligibilityGroupBlockProps,
): JSX.Element {
  const {
    group,
    fields,
    engine,
    touchedFieldIds,
    onTouchKey,
    issuesByFieldId,
  } = props;

  // When any field in this group has touched validation
  // issues (e.g., user clicked Continue, the section flushed touched,
  // and required-missing issues surfaced), force the block into
  // 'expanded'. Without this override, the user sees the section-
  // level "X questions need an answer" message but the questions
  // themselves are hidden behind a collapsed group — they cannot
  // reach them. We don't mutate `blockState` itself; the user's
  // explicit choice (clicking "None apply" vs. "Yes show me") is
  // preserved for after the issues are resolved.
  const groupHasIssues = useMemo(() => {
    for (const f of fields) {
      const issues = issuesByFieldId.get(f.id) ?? [];
      if (issues.length > 0) return true;
    }
    return false;
  }, [fields, issuesByFieldId]);

  // Initial state derives from the engine. Resumed sessions where
  // all answers are No get the collapsed "all_no" state; sessions
  // with any Yes get expanded; fresh sessions stay unanswered.
  const [blockState, setBlockState] = useState<TBlockState>(() =>
    deriveInitialState(engine, fields),
  );

  // While the block is still 'unanswered' (user hasn't taken a
  // deliberate action yet), watch the engine for late answers — e.g.,
  // hydrate completing AFTER mount, or another tab/path writing
  // answers via the engine subscription. As soon as the user clicks
  // a button, the state transitions out of 'unanswered' and we stop
  // overriding their choice. Re-derive on `fields` changes too so
  // visibility-rule-driven additions/removals re-evaluate the block.
  useEffect(() => {
    if (blockState !== 'unanswered') return undefined;
    // Sync once now in case the engine already has answers we missed.
    const initialDerived = deriveInitialState(engine, fields);
    if (initialDerived !== 'unanswered') {
      setBlockState(initialDerived);
      return undefined;
    }
    // Subscribe so a later engine change can promote us.
    const unsub = engine.subscribe(() => {
      const derived = deriveInitialState(engine, fields);
      if (derived !== 'unanswered') {
        setBlockState(derived);
      }
    });
    return unsub;
  }, [engine, fields, blockState]);

  const handleNoneApply = (): void => {
    for (const field of fields) {
      engine.setAnswer(field.id as FieldId, { type: 'boolean', value: false });
      onTouchKey(field.id);
    }
    setBlockState('all_no');
  };

  const handleExpand = (): void => {
    setBlockState('expanded');
  };

  const handleCollapse = (): void => {
    // Always collapse to the safest non-expanded state:
    //   - If every question is answered No → 'all_no' (the green-check
    //     summary chip with Edit link).
    //   - Otherwise → 'unanswered' (the two big Yes-show / No-none-apply
    //     buttons). This includes the case where some answers are Yes:
    //     we still re-collapse and surface the choice again rather than
    //     silently hiding affirmative answers.
    // The previous logic delegated to deriveInitialState, which returns
    // 'expanded' when any field has a Yes answer — meaning clicking
    // Collapse on a section with one Yes silently did nothing.
    const allAnsweredNo = fields.every((field) => {
      const v = engine.getAnswer(field.id);
      return v?.type === 'boolean' && v.value === false;
    });
    setBlockState(allAnsweredNo ? 'all_no' : 'unanswered');
  };

  const fieldCount = fields.length;

  // Effective render state: groupHasIssues overrides to 'expanded' so
  // applicants can actually reach the missing questions after Continue.
  const effectiveState: TBlockState = groupHasIssues ? 'expanded' : blockState;

  if (effectiveState === 'unanswered') {
    return (
      <div
        className={styles.group}
        data-eligibility-group={group.id}
        data-state="unanswered"
      >
        <div className={styles.group__header}>
          <h3 className={styles.group__label}>{group.label}</h3>
          <span className={styles.group__count}>
            {fieldCount} question{fieldCount === 1 ? '' : 's'}
          </span>
        </div>
        <p className={styles.group__description}>{group.description}</p>
        <div className={styles.group__actions}>
          <button
            type="button"
            className={styles.group__action_secondary}
            onClick={handleExpand}
          >
            Yes — show me
          </button>
          <button
            type="button"
            className={styles.group__action_primary}
            onClick={handleNoneApply}
          >
            No — none apply
          </button>
        </div>
      </div>
    );
  }

  if (effectiveState === 'all_no') {
    return (
      <div
        className={styles.group}
        data-eligibility-group={group.id}
        data-state="all_no"
      >
        <div className={styles.group__header}>
          <h3 className={styles.group__label}>{group.label}</h3>
          <span className={styles.group__answered_chip}>
            <span aria-hidden="true" className={styles.group__answered_dot} />
            None apply
          </span>
        </div>
        <div className={styles.group__actions}>
          <button
            type="button"
            className={styles.group__edit_link}
            onClick={handleExpand}
          >
            Edit
          </button>
        </div>
      </div>
    );
  }

  // Expanded: render each field individually.
  return (
    <div
      className={styles.group}
      data-eligibility-group={group.id}
      data-state="expanded"
    >
      <div className={styles.group__header}>
        <h3 className={styles.group__label}>{group.label}</h3>
        <button
          type="button"
          className={styles.group__edit_link}
          onClick={handleCollapse}
        >
          Collapse
        </button>
      </div>
      <p className={styles.group__description}>{group.description}</p>
      <div className={styles.group__fields}>
        {fields.map(field => (
          <FieldRenderer
            key={field.id}
            field={withUserHelpText(field)}
            engine={engine}
            touched={touchedFieldIds.has(field.id)}
            onTouch={() => onTouchKey(field.id)}
          />
        ))}
      </div>
    </div>
  );
}
